import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Skeleton from "react-loading-skeleton";
import { ChevronRight, Star, Clock, Utensils, ImageOff } from "lucide-react";
import { listMyWishesPage } from "../../services/wish.api";
import { WISH_STATUSES } from "../../services/normalizers";
import LoadMore from "../shared/LoadMore";
import Recipes from "../home/sections/Recipes";
import "./my-wishes.css";

const fmtDate = (v) => {
  if (!v) return "N/A";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "N/A";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

/** Keyed by the API's own wish statuses, plus the derived "Ordered". */
const STATUS_CFG = {
  Pending:     { bg: "#fef3c7", color: "#92400e", dot: "#f59e0b" },
  Negotiating: { bg: "#e0e7ff", color: "#3730a3", dot: "#6366f1" },
  Accepted:    { bg: "#dcfce7", color: "#166534", dot: "#059669" },
  Rejected:    { bg: "#fee2e2", color: "#991b1b", dot: "#ef4444" },
  Expired:     { bg: "#f1f5f9", color: "#475569", dot: "#94a3b8" },
  Ordered:     { bg: "#eff6ff", color: "#1e40af", dot: "#0d6efd" },
};

/**
 * Each tab maps to exactly one status the list endpoint accepts, so filtering
 * and pagination both happen server-side.
 */
const TABS = [
  { key: "all", label: "All", status: null },
  ...WISH_STATUSES.map((s) => ({ key: s.code, label: s.label, status: s.code })),
];

/* ── WishCard ─────────────────────────────────── */
function WishCard({ item }) {
  const cfg = STATUS_CFG[item.wishStatus] || STATUS_CFG.Pending;
  const [imgError, setImgError] = useState(false);
  const hasImg = item.wishImage && !imgError;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      className="wish-card"
    >
      {/* Image area — always 170px tall */}
      <div className="wish-card-img-wrap">
        {hasImg ? (
          <img
            src={item.wishImage}
            alt={item.wishName}
            className="wish-card-img"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="wish-card-img-placeholder">
            <ImageOff size={32} color="#d1d5db" />
            <span>No image</span>
          </div>
        )}

        {/* Status chip */}
        <span className="wish-status-chip" style={{ background: cfg.bg, color: cfg.color }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.dot, flexShrink: 0, display: "inline-block" }} />
          {item.wishStatus}
        </span>
      </div>

      {/* Content */}
      <div className="wish-card-body">
        <p className="wish-card-name">{item.wishName}</p>
        <div className="wish-card-meta">
          <Clock size={11} color="#9ca3af" />
          <span>{item.wishDate}</span>
        </div>
        <Link
          to={`/wish-order-pricing?wishId=${item.id}`}
          state={[item.raw]}
          className="wish-card-btn"
        >
          View Details <ChevronRight size={13} />
        </Link>
      </div>
    </motion.div>
  );
}

/* ── WishCard Skeleton ────────────────────────── */
function WishCardSkeleton() {
  return (
    <div className="wish-card">
      <Skeleton height={170} style={{ display: "block" }} />
      <div className="wish-card-body">
        <Skeleton height={16} width="75%" style={{ marginBottom: 8 }} />
        <Skeleton height={12} width="45%" style={{ marginBottom: 14 }} />
        <Skeleton height={32} borderRadius={8} />
      </div>
    </div>
  );
}

/* ── Page ─────────────────────────────────────── */
export default function MyWishes() {
  const [tab, setTab] = useState("all");
  const activeTab = TABS.find((t) => t.key === tab) || TABS[0];

  const {
    data,
    isPending,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    // The status is part of the key, so switching tabs starts a fresh page 1.
    queryKey: ["buyerWishes", activeTab.status],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      listMyWishesPage({
        page: pageParam,
        limit: 20,
        status: activeTab.status || undefined,
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextPage : undefined),
  });

  const wishes = useMemo(
    () => (data?.pages || []).flatMap((p) => p.items),
    [data],
  );

  const filtered = useMemo(() => wishes.map(w => ({
    id: w?._id,
    wishName: w?.dishName || "Untitled Wish",
    wishDate: fmtDate(w?.createdAt || w?.desiredDate),
    // The card shows the real status the API reported.
    wishStatus: w?.statusLabel || "Pending",
    wishImage: w?.photos?.[0] || null,
    raw: w,
  })), [wishes]);

  const total = data?.pages?.[0]?.pagination?.total ?? filtered.length;

  return (
    <div className="home-content-shell page-navbar-space pb-5">

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <div>
          <h5 style={{ fontWeight: 800, fontSize: 22, margin: "0 0 3px", fontFamily: "'Inter', sans-serif", color: "#1a1a2e" }}>
            My Wishes
          </h5>
          <p style={{ margin: 0, fontSize: 13, color: "#9ca3af", fontFamily: "'Inter', sans-serif" }}>
            Track your custom food requests and negotiations
          </p>
        </div>
        <Link to="/wish-order" style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          padding: "9px 18px", borderRadius: 10,
          background: "#0d6efd", color: "#fff",
          fontSize: 13, fontWeight: 600, textDecoration: "none",
          fontFamily: "'Inter', sans-serif",
        }}>
          <Utensils size={14} /> New Wish
        </Link>
      </div>

      {/* Status tabs — always available, so an empty tab is never a dead end */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, overflowX: "auto", paddingBottom: 2, scrollbarWidth: "none" }}>
        {TABS.map(t => {
          const active = tab === t.key;
          const cfg = STATUS_CFG[t.label];
          return (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              flexShrink: 0, padding: "7px 16px", borderRadius: 20,
              border: active ? "none" : "1.5px solid #e5e7eb",
              background: active ? (cfg ? cfg.dot : "#0d6efd") : "#fff",
              color: active ? "#fff" : "#374151",
              fontSize: 13, fontWeight: 600,
              fontFamily: "'Inter', sans-serif", cursor: "pointer",
            }}>
              {t.label}
              {active && total > 0 && (
                <span style={{
                  marginLeft: 6,
                  background: "rgba(255,255,255,0.25)", color: "#fff",
                  borderRadius: 20, padding: "1px 7px",
                  fontSize: 11, fontWeight: 700,
                }}>{total}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: "12px 16px", borderRadius: 10, background: "#fee2e2", color: "#991b1b", fontSize: 13, fontFamily: "'Inter', sans-serif", marginBottom: 16 }}>
          Failed to load your wish orders. Please try again.
        </div>
      )}

      {/* Grid */}
      <div className="wishes-grid">
        {isPending
          ? [1,2,3,4].map(i => <WishCardSkeleton key={i} />)
          : <AnimatePresence mode="popLayout">
              {filtered.map(item => <WishCard key={item.id} item={item} />)}
            </AnimatePresence>
        }
      </div>

      {!isPending && (
        <LoadMore
          hasMore={hasNextPage}
          isLoading={isFetchingNextPage}
          onClick={() => fetchNextPage()}
          label="Load older wishes"
        />
      )}

      {/* Tab empty */}
      {!isPending && tab !== "all" && filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af", fontFamily: "'Inter', sans-serif", fontSize: 14 }}>
          No {activeTab.label.toLowerCase()} wishes
        </div>
      )}

      {/* Empty state */}
      {!isPending && tab === "all" && filtered.length === 0 && (
        <div style={{
          textAlign: "center", padding: "60px 20px",
          fontFamily: "'Inter', sans-serif",
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: "50%",
            background: "#f1f5f9", margin: "0 auto 16px",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Star size={28} color="#d1d5db" />
          </div>
          <p style={{ fontSize: 15, fontWeight: 600, color: "#374151", margin: "0 0 6px" }}>No wishes yet</p>
          <p style={{ fontSize: 13, color: "#9ca3af", margin: "0 0 20px" }}>
            Submit your first custom food request from any kitchen profile.
          </p>
          <Link to="/wish-order" style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "10px 22px", borderRadius: 8,
            background: "#0d6efd", color: "#fff",
            fontSize: 13, fontWeight: 600, textDecoration: "none",
          }}>
            <Utensils size={14} /> Make a Wish
          </Link>
        </div>
      )}

      {/* Popular Recipes */}
      <div style={{ marginTop: 48 }}>
        <Recipes title="Popular Recipes" />
      </div>
    </div>
  );
}
